import { PencilSimpleIcon, TrashIcon } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import { TimeBlockNote } from '@/api/reservations/get-time-block-notes'
import { Button } from '@/components/ui/button'
import { Divider } from '@/components/ui/divider'
import { Flex } from '@/components/ui/flex'
import { Icon } from '@/components/ui/icon'
import { Typography } from '@/components/ui/typography'
import { USER_ROLE_LABEL_MAP } from '@/models/features/user.models'
import { formatDateTimeInTimezone } from '@/utils/date'

import * as S from './time-block-notes-modal.styled'

interface TimeBlockNoteItemProps {
  note: TimeBlockNote
  canManage: boolean
  timezone: string
  onEdit: (note: TimeBlockNote) => void
  onDelete: (note: TimeBlockNote) => void
}

export const TimeBlockNoteItem = ({
  note,
  canManage,
  timezone,
  onEdit,
  onDelete,
}: TimeBlockNoteItemProps) => {
  const { t } = useTranslation(['reservations'])
  const authorName = note.createdBy
    ? `${note.createdBy.firstName} ${note.createdBy.lastName}`
    : t('reservations:timeBlocks.notesModal.item.deletedUser')

  return (
    <S.NoteCard>
      <Flex align="center" gap={8} justify="space-between">
        <Typography as="span" color="black800" size="text-medium" weight="semibold">
          {note.title}
        </Typography>
        {canManage && (
          <Flex gap={8} shrink={0}>
            <Button
              onlyBox
              aria-label={t('reservations:timeBlocks.notesModal.item.a11y.edit')}
              size="small"
              use="secondary"
              onClick={() => onEdit(note)}
            >
              <Icon icon={PencilSimpleIcon} />
            </Button>
            <Button
              onlyBox
              aria-label={t('reservations:timeBlocks.notesModal.item.a11y.delete')}
              size="small"
              use="secondary"
              onClick={() => onDelete(note)}
            >
              <Icon icon={TrashIcon} color="red500" />
            </Button>
          </Flex>
        )}
      </Flex>
      <Typography as="p" color="black700" size="text-medium" wordBreak="break-word">
        {note.noteBody}
      </Typography>
      <Divider />
      <Flex align="center" gap={8} paddingY="4 0">
        <Typography as="span" color="black500" size="text-medium">
          {authorName}
          {note.createdBy?.role ? `, ${USER_ROLE_LABEL_MAP[note.createdBy.role]}` : ''},{' '}
          {formatDateTimeInTimezone(note.createdAt, timezone)}
        </Typography>
      </Flex>
    </S.NoteCard>
  )
}
